import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../../context/authcontext";
import { ProposalService, ProposalRequest } from "./proposal.service";
import Proposals from "./index";
import ProposalsGrid from "./proposalsGrid";
import { useTenantChange } from "../../../hooks/useTenantChange";
import { API_BASE_URL } from "../../../environment";

const ProposalsContainer = () => {
  const { selectedTenantId } = useContext(AuthContext);
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");

  const [proposals, setProposals] = useState<any[]>([]);
  const [proposalsCount, setProposalsCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenantUsers, setTenantUsers] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalPages, setTotalPages] = useState(1);

  const clearProposalsData = useCallback(() => {
    setProposals([]);
    setProposalsCount(null);
    setError(null);
    setCurrentPage(1);
    setPageSize(12);
    setTotalPages(1);
    setIsInitialized(false);
  }, []);

  useTenantChange({
    onTenantChange: () => {
      clearProposalsData();
    },
    refreshData: () => {
      if (selectedTenantId) {
        fetchProposals(1, pageSize);
      }
    },
    clearData: clearProposalsData,
  });

  const fetchProposals = useCallback(
    async (
      page: number = 1,
      limit: number = 12,
      filterParams: Record<string, any> = {}
    ) => {
      if (!selectedTenantId) return;

      setLoading(true);
      setError(null);
      try {
        const data = await ProposalService.getProposals(
          Number(selectedTenantId),
          page,
          limit,
          filterParams
        );
        setProposals(data.results);
        setProposalsCount(data.count);
        setTotalPages(Math.ceil(data.count / limit));
        setBackgroundLoading(true);
        setIsInitialized(true);
        setLastFetched(new Date());
      } catch (err) {
        console.error("Error fetching proposals:", err);
        setError(
          err instanceof Error ? err.message : "Failed to fetch proposals"
        );
      } finally {
        setLoading(false);
      }
    },
    [selectedTenantId]
  );

  useEffect(() => {
    if (selectedTenantId) {
      fetchProposals(1, pageSize);
    }
  }, [selectedTenantId, fetchProposals, pageSize]);

  const addProposal = useCallback((proposal: any) => {
    setProposals((prev) => [proposal, ...prev]);
    setProposalsCount((prev) => (prev || 0) + 1);
  }, []);

  const updateProposal = useCallback((updatedProposal: any) => {
    setProposals((prev) =>
      prev.map((proposal) =>
        proposal.proposal_id === updatedProposal.proposal_id
          ? updatedProposal
          : proposal
      )
    );
  }, []);

  const removeProposal = useCallback((proposalId: number) => {
    setProposals((prev) =>
      prev.filter((proposal) => proposal.proposal_id !== proposalId)
    );
    setProposalsCount((prev) => Math.max(0, (prev || 1) - 1));
  }, []);

  const refreshProposals = useCallback(async () => {
    await fetchProposals(currentPage, pageSize);
  }, [fetchProposals, currentPage, pageSize]);

  const clearBackgroundLoading = useCallback(() => {
    setBackgroundLoading(false);
  }, []);

  const handleViewSwitch = (view: "list" | "grid") => {
    setViewMode(view);
  };

  const pagination = {
    currentPage,
    pageSize,
    totalCount: proposalsCount || 0,
    totalPages,
  };

  const sharedProps = {
    proposals,
    loading,
    backgroundLoading,
    error,
    pagination,
    fetchProposals,
    refreshProposals,
    addProposal,
    updateProposal,
    removeProposal,
    lastFetched,
    isInitialized,
    currentView: viewMode,
    onViewSwitch: handleViewSwitch,
    clearBackgroundLoading,
  };

  return (
    <>
      {viewMode === "list" ? (
        <Proposals {...sharedProps} />
      ) : (
        <ProposalsGrid {...sharedProps} />
      )}
    </>
  );
};

export default ProposalsContainer;
